import {
  SASBOSS_ENTERPRISES_LOADING,
  SASBOSS_ENTERPRISES_LOADED,
  SASBOSS_ENTERPRISES_FAILED,
  SASBOSS_ENTERPRISE_UPDATING,
  SASBOSS_ENTERPRISE_UPDATED,
  SASBOSS_ENTERPRISE_UPDATE_FAILED,
  SASBOSS_SYNC_TRIGGERING,
  SASBOSS_SYNC_TRIGGERED,
  SASBOSS_SYNC_TRIGGER_FAILED,
} from './sasbossDidTypes'
import api from '../../services/api'
import { addErrorToast, addSaveToast } from './companyActionCreators'

// ─── Enterprises ───────────────────────────────────────────────────────

export const fetchSasbossEnterprises = () => async (dispatch) => {
  try {
    dispatch({ type: SASBOSS_ENTERPRISES_LOADING })
    const res = await api.get('/sasboss/enterprises')
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: SASBOSS_ENTERPRISES_LOADED, payload: data })
  } catch (err) {
    dispatch({ type: SASBOSS_ENTERPRISES_FAILED })
    dispatch(addErrorToast('Failed to load SASBOSS enterprises. Check the SASBOSS connection.'))
  }
}

// ─── CW company search (type-ahead for the mapping picker) ──────────────

export const searchSasbossCompanies = (query, limit = 20) => async (dispatch) => {
  if (!query || !query.trim()) return []
  try {
    const params = new URLSearchParams({ q: query, limit: String(limit) })
    const res = await api.get(`/sasboss/companies/search?${params.toString()}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json()
  } catch (err) {
    dispatch(addErrorToast('Company search failed'))
    return []
  }
}

// ─── Mapping ────────────────────────────────────────────────────────────

export const mapSasbossEnterprise = (enterpriseId, enterpriseName, cwCompanyId) => async (dispatch) => {
  try {
    dispatch({ type: SASBOSS_ENTERPRISE_UPDATING, payload: enterpriseId })
    const res = await api.post(`/sasboss/enterprises/${encodeURIComponent(enterpriseId)}/map-cw`, {
      cwCompanyId,
      enterpriseName,
    })
    if (!res.ok) {
      const message = await res.text()
      throw new Error(message || `HTTP ${res.status}`)
    }
    dispatch({ type: SASBOSS_ENTERPRISE_UPDATED, payload: enterpriseId })
    dispatch(addSaveToast('Enterprise mapped to ConnectWise company'))
    dispatch(fetchSasbossEnterprises())
  } catch (err) {
    dispatch({ type: SASBOSS_ENTERPRISE_UPDATE_FAILED, payload: enterpriseId })
    dispatch(addErrorToast(err.message || 'Failed to map enterprise'))
  }
}

export const unmapSasbossEnterprise = (enterpriseId) => async (dispatch) => {
  try {
    dispatch({ type: SASBOSS_ENTERPRISE_UPDATING, payload: enterpriseId })
    const res = await api.deleteCall(`/sasboss/enterprises/${encodeURIComponent(enterpriseId)}/map`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    dispatch({ type: SASBOSS_ENTERPRISE_UPDATED, payload: enterpriseId })
    dispatch(addSaveToast('Enterprise unmapped'))
    dispatch(fetchSasbossEnterprises())
  } catch (err) {
    dispatch({ type: SASBOSS_ENTERPRISE_UPDATE_FAILED, payload: enterpriseId })
    dispatch(addErrorToast('Failed to unmap enterprise'))
  }
}

// ─── Sync trigger ────────────────────────────────────────────────────────

export const triggerSasbossSync = () => async (dispatch) => {
  try {
    dispatch({ type: SASBOSS_SYNC_TRIGGERING })
    const res = await api.post('/sasboss/sync')
    if (res.status === 409) {
      dispatch({ type: SASBOSS_SYNC_TRIGGER_FAILED })
      dispatch(addErrorToast('A SASBOSS DID sync is already running.'))
      return
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dispatch({ type: SASBOSS_SYNC_TRIGGERED, payload: data })
    dispatch(addSaveToast('SASBOSS DID sync queued — check the Hangfire dashboard for progress'))
  } catch (err) {
    dispatch({ type: SASBOSS_SYNC_TRIGGER_FAILED })
    dispatch(addErrorToast('Failed to trigger SASBOSS DID sync'))
  }
}

// ─── DID export ──────────────────────────────────────────────────────────

// The API needs the bearer token, so this cannot be a plain link — the response is
// read as a blob and handed to a temporary anchor.
export const exportSasbossDidNumbers = (enterpriseId, enterpriseName) => async (dispatch) => {
  try {
    const res = await api.get(`/sasboss/enterprises/${encodeURIComponent(enterpriseId)}/did-export`)
    if (res.status === 409) {
      const body = await res.json()
      dispatch(addErrorToast(body?.error || 'No DID inventory has been loaded yet.'))
      return
    }
    if (!res.ok) throw new Error(`HTTP ${res.status}`)

    const blob = await res.blob()
    const fileName = fileNameFrom(res) || `did-numbers-${enterpriseId}.csv`

    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    link.remove()
    // Deferred: revoking in the same tick has historically cancelled the download in Safari.
    setTimeout(() => window.URL.revokeObjectURL(url), 0)

    dispatch(addSaveToast(`Exported DID numbers for ${enterpriseName || enterpriseId}`))
  } catch (err) {
    dispatch(addErrorToast('Failed to export DID numbers'))
  }
}

// Prefer the server's name so the two cannot drift. Handles the quoted and bare forms.
const fileNameFrom = (res) => {
  const header = res.headers.get('content-disposition')
  if (!header) return null
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header)
  return match ? decodeURIComponent(match[1]) : null
}
